import {
  flip,
  offset,
  shift,
  useClick,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { Portal, Transition } from "@headlessui/react";
import { FC, PropsWithChildren, ReactNode, useState } from "react";
import { calculateClasses, OverridableClassName } from "utils/style";

const CONTAINER_CLS = "";
const TOOLTIP_CLS =
  "rounded-md bg-gray-50 px-3 py-1.5 text-xs font-normal text-gray-600 shadow-lg ring-1 ring-gray-500/30 z-30";

type TooltipProps = {
  content: ReactNode;
  className?: OverridableClassName<typeof CONTAINER_CLS>;
  tooltipClassName?: OverridableClassName<typeof TOOLTIP_CLS>;
  tooltipOffset?: number;
  event?: "hover" | "click";
  disabled?: boolean;
  delay?: number;
};

const Tooltip: FC<PropsWithChildren<TooltipProps>> = ({
  content,
  children,
  className,
  tooltipOffset = 5,
  tooltipClassName,
  event = "hover",
  disabled = false,
  delay = 800,
}) => {
  const [open, setOpen] = useState(false);
  const { x, y, refs, strategy, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-start",
    middleware: [flip(), offset(tooltipOffset), shift()],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { delay, enabled: event === "hover" && !disabled }),
    useClick(context, { enabled: event === "click" && !disabled }),
  ]);

  const containerCls = calculateClasses(className, CONTAINER_CLS);
  const tooltipCls = calculateClasses(tooltipClassName, TOOLTIP_CLS);

  return (
    <>
      <div
        className={containerCls}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {children}
      </div>
      <Portal>
        <Transition
          show={open}
          enter="transition-opacity duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          ref={refs.setFloating}
          style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          className={tooltipCls}
          {...getFloatingProps()}
        >
          {content}
        </Transition>
      </Portal>
    </>
  );
};

export default Tooltip;
